// src/components/crm/UsersManagement.js

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, functions } from '../../firebase/config';
import { collection, query, where, onSnapshot, updateDoc, doc, serverTimestamp, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions'; 
import { Search, Plus, Edit, Trash2, ArrowUp, ArrowDown, CreditCard, UserPlus, UserCheck, UserX, ChevronDown, ChevronUp, Send, Check } from 'lucide-react';
import EmployeeProfileModal from './EmployeeProfileModal';
import TeamMemberModal, { ActivateTeamMemberModal } from './TeamMemberModal';
import ConfirmationModal from '../common/ConfirmationModal';

const UsersManagement = ({ currentUser, currentUserProfile }) => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [userToRemove, setUserToRemove] = useState(null);
    const [statusBusyId, setStatusBusyId] = useState(null);
    const [resendBusyId, setResendBusyId] = useState(null); // r1-resend-invite-3f8c9a2d — per-row busy key
    const [resendSentId, setResendSentId] = useState(null); // transient "Sent" confirmation, keyed by user id
    const resendTimerRef = useRef(null);                    // pending "Sent" timeout handle
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });
    const [isPortalLoading, setIsPortalLoading] = useState(false);

    // --- FIELD WORKER STATE ---
    const [fieldWorkers, setFieldWorkers]                     = useState([]);
    const [orgOwnerUid, setOrgOwnerUid]                       = useState(null);
    const [isFieldWorkerModalOpen, setIsFieldWorkerModalOpen] = useState(false);
    const [editingFieldWorker, setEditingFieldWorker]         = useState(null);
    const [activatingFieldWorker, setActivatingFieldWorker]   = useState(null);
    const [showFieldWorkers, setShowFieldWorkers]             = useState(true);

    // --- 1. DEFINE ROLE HIERARCHY ---
    const OWNER_ROLES = ['Owner', 'Owner/System Admin', 'Owner / System Admin', 'System Admin'];
    const ADMIN_ROLES = ['Admin'];

    // Helper booleans
    const isCurrentUserOwner = currentUserProfile && OWNER_ROLES.includes(currentUserProfile.role);
    const isCurrentUserAdmin = currentUserProfile && ADMIN_ROLES.includes(currentUserProfile.role);
    // r1-resend-invite-3f8c9a2d — resend-invite gate mirrors the server's Add-User role set
    // (Admin / Manager / Owner). Manager is NOT in the arrays above, so add it explicitly.
    const canResendInvite = currentUserProfile && [...OWNER_ROLES, ...ADMIN_ROLES, 'Manager'].includes(currentUserProfile.role);

    // Seats consumed = users minus non-consuming statuses (mirrors backend rule r1-seat-rule-a3f9c21e)
    const consumedSeats = users.filter(u => !['inactive', 'downgraded', 'removed', 'terminated', 'resigned'].includes(u.status)).length;

    // Team Members list hides anyone upgraded to a User (status 'activated') — they already
    // appear up top in Users, so each person shows in exactly one place. r3-dedupe-6f2a8b91
    const visibleTeamMembers = fieldWorkers.filter(fw => fw.status !== 'activated');

    useEffect(() => {
        if (!currentUserProfile?.organizationId) {
            setIsLoading(false);
            return;
        }

        // Fetch the org's original owner UID so we can protect them from deletion
        getDoc(doc(db, 'organizations', currentUserProfile.organizationId)).then(orgSnap => {
            if (orgSnap.exists()) setOrgOwnerUid(orgSnap.data().ownerUid || null);
        }).catch(() => {});

        const usersRef = collection(db, 'users');
        const q = query(usersRef, where("organizationId", "==", currentUserProfile.organizationId));
        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const usersData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })).filter(user => !['removed', 'downgraded'].includes(user.status));

                setUsers(usersData);
                setIsLoading(false);
            },
            (error) => {
                console.error("Error fetching users:", error);
                setError("Failed to load users: " + error.message);
                setIsLoading(false);
            }
        );

        return unsubscribe;
    }, [currentUserProfile]);

    // --- FIELD WORKERS LISTENER ---
    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;
        const q = query(
            collection(db, `organizations/${orgId}/fieldWorkers`)
        );
        const unsub = onSnapshot(q, snap => {
            setFieldWorkers(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => (a.name||'').localeCompare(b.name||'')));
        }, err => {
            console.error('Error fetching field workers:', err);
        });
        return unsub;
    }, [currentUserProfile?.organizationId]);

    const filteredAndSortedUsers = useMemo(() => {
        let filteredUsers = [...users];
        if (searchQuery) {
            filteredUsers = filteredUsers.filter(user =>
                user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (sortConfig !== null) {
            filteredUsers.sort((a, b) => {
                const valA = a[sortConfig.key] || '';
                const valB = b[sortConfig.key] || '';
                if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return filteredUsers;
    }, [users, searchQuery, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const handleManageSubscription = async () => {
        setIsPortalLoading(true);
        try {
            const functionsInstance = getFunctions();
            const createPortalSession = httpsCallable(functionsInstance, 'createPortalSession');
            const { data } = await createPortalSession({ 
                returnUrl: window.location.href 
            });
            
            if (data.url) {
                window.location.href = data.url;
            }
        } catch (error) {
            console.error("Failed to load billing portal:", error);
            alert("Could not load billing portal. Please try again.");
            setIsPortalLoading(false);
        }
    };

    const handleAddClick = () => {
        const LIMITS = { 'solo': 1, 'starter': 5, 'growth': 10, 'scale': 20 };
        const currentTier = currentUserProfile?.planTier || 'solo';
        const limit = LIMITS[currentTier] || 1;

        if (consumedSeats >= limit) {
            const confirmUpgrade = window.confirm(
                `Limit Reached!\n\nYour '${currentTier}' plan allows ${limit} users, and you currently have ${consumedSeats}.\n\nWould you like to upgrade your plan now?`
            );
            if (confirmUpgrade) {
                handleManageSubscription();
            }
            return;
        }

        setEditingUser(null);
        setIsModalOpen(true);
    };

    const handleRemoveUser = async (userId) => {
        setIsProcessing(true);
        setError('');
        try {
            const deleteUser = httpsCallable(functions, 'deleteUser');
            await deleteUser({ uid: userId });
            setUserToRemove(null);
        } catch (error) {
            console.error("Error removing user:", error);
            setError(`Failed to remove user: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    // Deactivate/reactivate via the focused backend engine (never touches HR fields).
    const handleSetActiveStatus = async (user, active) => {
        if (!active) {
            const ok = window.confirm(
                `Deactivate ${user.name || user.email}?\n\nThey'll lose app access immediately and this frees up a seat. Their profile and history are kept — you can reactivate them anytime. No billing change.`
            );
            if (!ok) return;
        }
        setStatusBusyId(user.id);
        setError('');
        try {
            const setUserActiveStatus = httpsCallable(functions, 'setUserActiveStatus');
            await setUserActiveStatus({ uid: user.id, active });
        } catch (err) {
            console.error("Error changing user status:", err);
            setError(err?.message || 'Could not change user status.');
        } finally {
            setStatusBusyId(null);
        }
    };

    // r1-resend-invite-3f8c9a2d — clear any pending "Sent" timer on unmount so no setState
    // can fire on an unmounted component. (An onSnapshot re-render never unmounts this.)
    useEffect(() => () => { if (resendTimerRef.current) clearTimeout(resendTimerRef.current); }, []);

    // Resend the invitation email to a still-'invited' user. Mirrors handleSetActiveStatus's
    // busy/try/catch/finally; success shows a transient "Sent" for ~3s. r1-resend-invite-3f8c9a2d
    const handleResendInvite = async (user) => {
        setResendBusyId(user.id);
        setError('');
        try {
            const resendInvite = httpsCallable(functions, 'resendInvite');
            await resendInvite({ uid: user.id });
            // Clear any pending timer before starting a new one, so at most one is ever live.
            if (resendTimerRef.current) clearTimeout(resendTimerRef.current);
            setResendSentId(user.id);
            resendTimerRef.current = setTimeout(() => {
                setResendSentId(null);
                resendTimerRef.current = null;
            }, 3000);
        } catch (err) {
            console.error("Error resending invite:", err);
            setError(err?.message || 'Could not resend the invite.');
        } finally {
            setResendBusyId(null);
        }
    };

    // --- 2. HIERARCHY PERMISSION CHECK ---
    const canManageUser = (targetUser) => {
        if (!currentUserProfile) return false;
        
        // A. Owners can manage EVERYONE
        if (isCurrentUserOwner) return true;

        // B. Admins can manage everyone EXCEPT Owners
        if (isCurrentUserAdmin) {
            const targetIsOwner = OWNER_ROLES.includes(targetUser.role);
            if (targetIsOwner) return false; // Admin cannot touch Owner
            return true; // Admin CAN touch other Admins, Managers, Users
        }

        // C. Managers can manage their direct reports only
        if (currentUserProfile.role === 'Manager' && targetUser.managerId === currentUser.uid) return true;
        
        return false;
    };

    const getSortIndicator = (key) => {
        if (sortConfig.key !== key) return <span className="inline-block w-4"></span>;
        return sortConfig.direction === 'ascending' ?
            <ArrowUp className="inline-block ml-1 h-4 w-4" /> :
            <ArrowDown className="inline-block ml-1 h-4 w-4" />;
    };

    if (isLoading) {
        return (
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-bold mb-4">User Management</h2>
                <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p>Loading users...</p>
                </div>
            </div>
        );
    }

    const currentTier = currentUserProfile?.planTier || 'solo';
    const limitMap = { 'solo': 1, 'starter': 5, 'growth': 10, 'scale': 20 };
    const maxUsers = limitMap[currentTier] || 1;

    return (
        <>
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 mb-6">
                    <div>
                        <h2 className="text-xl font-bold">User Management</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Plan: <span className="font-semibold uppercase">{currentTier}</span> 
                            {' '}&bull;{' '}
                            Users: <span className={`${consumedSeats >= maxUsers ? 'text-red-600 font-bold' : 'text-gray-700'}`}>
                                {consumedSeats} / {maxUsers}
                            </span>
                        </p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-center gap-2 w-full xl:w-auto">
                        <div className="relative flex-grow w-full sm:w-auto">
                             <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                             <input
                                type="text"
                                placeholder="Search by name..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>

                        <button
                            onClick={handleManageSubscription}
                            disabled={isPortalLoading}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md font-medium hover:bg-gray-50 transition-colors w-full sm:w-auto"
                        >
                            {isPortalLoading ? 'Loading...' : (
                                <>
                                    <CreditCard size={16} /> <span className="whitespace-nowrap">Manage Plan</span>
                                </>
                            )}
                        </button>

                        <button 
                            onClick={handleAddClick} 
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 w-full sm:w-auto"
                        >
                            <Plus size={16} /> <span className="whitespace-nowrap">Add User</span>
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-600">{error}</p>
                        <button onClick={() => setError('')} className="mt-2 text-sm text-red-800 underline">Dismiss</button>
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs uppercase bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('email')}>Email {getSortIndicator('email')}</th>
                                <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('name')}>Name {getSortIndicator('name')}</th>
                                <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('role')}>Role {getSortIndicator('role')}</th>
                                <th className="px-6 py-3 cursor-pointer" onClick={() => requestSort('managerId')}>Manager {getSortIndicator('managerId')}</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3">Created</th>
                                <th className="px-6 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedUsers.map(user => {
                                const managerName = user.managerId ? users.find(u => u.id === user.managerId)?.name : 'N/A';
                                const canBeManaged = canManageUser(user);
                                
                                // FIX: Ensure user can ALWAYS edit themselves, and Owners can edit everyone
                                const canEdit = canBeManaged || user.id === currentUser.uid;

                                return (
                                <tr key={user.id} className={`border-b hover:bg-gray-50 ${user.status === 'inactive' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
                                    <td className="px-6 py-4 font-medium">{user.email}</td>
                                    <td className="px-6 py-4">{user.name || 'Not set'}</td>
                                    <td className="px-6 py-4">
                                        <span className="px-2 py-1 bg-gray-200 text-gray-800 rounded-full text-xs font-medium">
                                            {user.role || 'User'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">{managerName}</td>
                                    <td className="px-6 py-4">
                                        {user.status === 'inactive' ? (
                                            <span className="px-2 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">Inactive</span>
                                        ) : user.status === 'invited' ? (
                                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Pending</span>
                                        ) : (
                                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Active</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-gray-500">
                                        {user.createdAt?.toDate?.().toLocaleDateString() ||
                                         (user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown')}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            {/* r1-resend-invite-3f8c9a2d — resend only for still-'invited' users, gated to Admin/Manager/Owner */}
                                            {canResendInvite && user.status === 'invited' && (
                                                resendSentId === user.id ? (
                                                    <span className="flex items-center gap-1 text-green-600 text-xs font-medium p-1" title="Invitation re-sent">
                                                        <Check size={16}/> Sent
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => handleResendInvite(user)}
                                                        className="text-purple-600 hover:text-purple-800 disabled:text-gray-400 disabled:cursor-not-allowed text-sm p-1"
                                                        disabled={resendBusyId === user.id}
                                                        title="Resend invitation email"
                                                    >
                                                        <Send size={16}/>
                                                    </button>
                                                )
                                            )}
                                            {canEdit && (
                                                <button 
                                                    onClick={() => { setEditingUser(user); setIsModalOpen(true); }}
                                                    className="text-blue-600 hover:text-blue-800 font-medium text-sm p-1"
                                                >
                                                    <Edit size={16}/>
                                                </button>
                                            )}
                                            {canBeManaged && user.id !== currentUser.uid && user.id !== orgOwnerUid && (
                                                user.status === 'inactive' ? (
                                                    <button
                                                        onClick={() => handleSetActiveStatus(user, true)}
                                                        className="text-green-600 hover:text-green-800 disabled:text-gray-400 disabled:cursor-not-allowed text-sm p-1"
                                                        disabled={statusBusyId === user.id}
                                                        title="Reactivate — restores app access, uses a seat"
                                                    >
                                                        <UserCheck size={16}/>
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleSetActiveStatus(user, false)}
                                                        className="text-amber-600 hover:text-amber-800 disabled:text-gray-400 disabled:cursor-not-allowed text-sm p-1"
                                                        disabled={statusBusyId === user.id}
                                                        title="Deactivate — removes app access, frees a seat"
                                                    >
                                                        <UserX size={16}/>
                                                    </button>
                                                )
                                            )}
                                            {/* Delete Logic: You cannot delete yourself, AND you must have permission to manage the target, AND you cannot delete the original org owner */}
                                            {user.id === orgOwnerUid && (
                                                <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0" title="Original account owner — cannot be removed">
                                                    Account Owner
                                                </span>
                                            )}
                                            {user.id !== currentUser.uid && canBeManaged && user.id !== orgOwnerUid && (
                                                <button
                                                    onClick={() => setUserToRemove(user)}
                                                    className="text-red-600 hover:text-red-800 font-medium disabled:text-gray-400 disabled:cursor-not-allowed text-sm p-1"
                                                    disabled={isProcessing}
                                                >
                                                    <Trash2 size={16}/>
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )})}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <EmployeeProfileModal
                    closeModal={() => setIsModalOpen(false)}
                    editingEmployee={editingUser}
                    currentUserProfile={currentUserProfile}
                />
            )}

            <ConfirmationModal
                isOpen={!!userToRemove}
                onClose={() => !isProcessing && setUserToRemove(null)}
                onConfirm={() => handleRemoveUser(userToRemove.id)}
                title="Remove User & Reassign Data"
                message={`Are you sure you want to remove ${userToRemove?.email}? All their customer data will be reassigned to the Orphan User.`}
                confirmText={isProcessing ? "Processing..." : "Remove & Reassign"}
            >
                {userToRemove && (
                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="font-semibold text-yellow-800">{userToRemove.email}</p>
                        <p className="text-sm text-yellow-700">This will preserve all customer data but will remove the user's authentication account.</p>
                    </div>
                )}
            </ConfirmationModal>

            {/* ── FIELD WORKERS SECTION ──────────────────────────────────── */}
            <div className="bg-white p-6 rounded-lg shadow mt-6">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-bold">Team Members</h2>
                            <span className="text-xs font-medium px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                                No app login required
                            </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Field techs, office staff, drivers, subcontractors — anyone you want to schedule or route without giving app access. Don't count against your plan limit.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => { setEditingFieldWorker(null); setIsFieldWorkerModalOpen(true); }}
                            className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 text-white rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
                        >
                            <UserPlus size={15} /> Add Team Member
                        </button>
                        <button
                            onClick={() => setShowFieldWorkers(p => !p)}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            {showFieldWorkers ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                    </div>
                </div>

                {showFieldWorkers && (
                    <>
                        {visibleTeamMembers.length === 0 ? (
                            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                                <UserPlus size={24} className="text-gray-300 mx-auto mb-2" />
                                <p className="text-sm text-gray-400">No team members without login yet.</p>
                                <p className="text-xs text-gray-400 mt-1">Add field techs, office staff, drivers or subcontractors who don't need app access.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left">
                                    <thead className="text-xs uppercase bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3">Name</th>
                                            <th className="px-4 py-3">Type</th>
                                            <th className="px-4 py-3">Phone</th>
                                            <th className="px-4 py-3">Email</th>
                                            <th className="px-4 py-3">Status</th>
                                            <th className="px-4 py-3">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleTeamMembers.map(fw => (
                                            <tr key={fw.id} className={`border-b hover:bg-gray-50 ${['terminated','resigned'].includes(fw.employmentStatus) ? 'opacity-60 bg-red-50/30' : 'bg-white'}`}>
                                                <td className="px-4 py-3 font-medium text-sm">
                                                    {fw.name}
                                                    {fw.status === 'activated' && (
                                                        <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full uppercase tracking-wide">App</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-gray-500 text-sm">{fw.workerType || '—'}</td>
                                                <td className="px-4 py-3 text-gray-500 text-sm">{fw.phone || '—'}</td>
                                                <td className="px-4 py-3 text-gray-500 text-sm">{fw.email || '—'}</td>
                                                <td className="px-4 py-3">
                                                    {(() => {
                                                        const s = fw.employmentStatus || 'active';
                                                        const cfg = {
                                                            active:     'bg-green-100 text-green-700',
                                                            inactive:   'bg-gray-100 text-gray-600',
                                                            on_leave:   'bg-blue-100 text-blue-700',
                                                            terminated: 'bg-red-100 text-red-700',
                                                            resigned:   'bg-orange-100 text-orange-700',
                                                        }[s] || 'bg-gray-100 text-gray-500';
                                                        const label = s === 'on_leave' ? 'On Leave' : s.charAt(0).toUpperCase() + s.slice(1);
                                                        return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg}`}>{label}</span>;
                                                    })()}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-1.5">
                                                        <button
                                                            onClick={() => { setEditingFieldWorker(fw); setIsFieldWorkerModalOpen(true); }}
                                                            className="text-blue-600 hover:text-blue-800 p-1"
                                                            title="Edit"
                                                        >
                                                            <Edit size={15} />
                                                        </button>
                                                        {!['terminated','resigned'].includes(fw.employmentStatus) && fw.status !== 'activated' && (
                                                            <button
                                                                onClick={() => setActivatingFieldWorker(fw)}
                                                                className="text-green-600 hover:text-green-800 p-1"
                                                                title="Activate as App User"
                                                            >
                                                                <UserCheck size={15} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Field Worker Modal */}
            {isFieldWorkerModalOpen && (
                <TeamMemberModal
                    onClose={() => {
                        setIsFieldWorkerModalOpen(false);
                        setEditingFieldWorker(null);
                    }}
                    editingTeamMember={editingFieldWorker}
                    currentUserProfile={currentUserProfile}
                />
            )}

            {/* Activate Field Worker Modal */}
            {activatingFieldWorker && (
                <ActivateTeamMemberModal
                    teamMember={activatingFieldWorker}
                    currentUserProfile={currentUserProfile}
                    onClose={() => setActivatingFieldWorker(null)}
                />
            )}
        </>
    );
};

export default UsersManagement;